import { useQuery } from "@tanstack/react-query";

import { useAgentExecutionClient } from "src/api/agents/execution";
import type { SessionResponse } from "src/clients/agent-management";

/**
 * Which endpoint family a session lives behind. Agent and copilot sessions
 * share one backend table but are namespaced by URL prefix, and a
 * cross-namespace read 404s by design — so the caller must pick the prefix.
 */
export type SessionNamespace = "agent" | "copilot";

const PREFIX_CACHE_KEY = "session-debug";

const debugKeys = {
  session: (wsId: string, kind: SessionNamespace, sessionId: string) =>
    [PREFIX_CACHE_KEY, wsId, kind, "session", sessionId] as const,
};

/**
 * One-shot read of a finished session's full trace. Deliberately not polling:
 * debugging is supposed to be used for historical sessions only.
 */
export const useDebugSession = (
  sessionId: string | null,
  kind: SessionNamespace,
) => {
  const { client, workspace } = useAgentExecutionClient();
  const wsId = workspace?.id ?? "";

  return useQuery({
    queryKey: debugKeys.session(wsId, kind, sessionId ?? ""),
    queryFn: async (): Promise<SessionResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!sessionId) {
        throw new Error("Session ID is required");
      }
      const response =
        kind === "copilot"
          ? await client.getSessionCopilotsManagementApiV0SessionsSessionIdGet({
              sessionId,
              includeMessages: true,
            })
          : await client.getSessionAgentsManagementApiV0SessionsSessionIdGet({
              sessionId,
              includeMessages: true,
            });
      return response.data;
    },
    enabled: !!client && !!sessionId,
    throwOnError: false,
  });
};
